export type SharedHouseholdStatus = 'none' | 'invited' | 'active';

export type SharedHouseholdLike = {
  id: string;
  inviteCode: string;
  ownerUserId: string;
  ownerDisplayName: string;
  memberUserId: string | null;
  memberDisplayName: string | null;
  status: 'invited' | 'active';
  createdAt: string;
};

export type SharedHouseholdSummary = {
  state: SharedHouseholdStatus;
  statusLabel: string;
  headline: string;
  ownershipLabel: string | null;
  body: string;
  nextStep: string;
  peerName: string;
  sharedScopeTitle: string;
  sharedScopeItems: string[];
  privateScopeTitle: string;
  privateScopeItems: string[];
  familySummary: string;
  moneySummary: string;
};

type SharedHouseholdSummaryInput = {
  share: SharedHouseholdLike | null;
  currentUserId?: string | null;
  partnerName?: string | null;
};

function resolvePeerName(input: SharedHouseholdSummaryInput) {
  const fallback = input.partnerName?.trim() || 'your other adult';
  if (!input.share) return fallback;

  if (!input.currentUserId) {
    return input.share.memberDisplayName || input.share.ownerDisplayName || fallback;
  }

  if (input.share.ownerUserId === input.currentUserId) {
    return input.share.memberDisplayName || fallback;
  }

  if (input.share.memberUserId === input.currentUserId) {
    return input.share.ownerDisplayName || fallback;
  }

  return input.share.memberDisplayName || input.share.ownerDisplayName || fallback;
}

const PRIVATE_SCOPE_ITEMS = [
  'Budgets and budget edits',
  'Connected bank imports',
  'Anything not marked Household',
];

export function buildHouseholdSharingSummary(input: SharedHouseholdSummaryInput): SharedHouseholdSummary {
  const peerName = resolvePeerName(input);

  if (!input.share) {
    const partnerSaved = Boolean(input.partnerName?.trim());
    return {
      state: 'none',
      statusLabel: partnerSaved ? 'Partner saved' : 'Not linked yet',
      headline: partnerSaved
        ? `${peerName} is saved, but the shared household trail is still optional.`
        : 'No shared household link yet.',
      ownershipLabel: null,
      body:
        'You can keep using Adulting on your own. Link one other adult only when you want chores, meal plans, and chosen Money items to follow the same household trail.',
      nextStep: 'Create a household code or join the other adult’s code later in My Settings when you are ready.',
      peerName,
      sharedScopeTitle: 'Shares once linked',
      sharedScopeItems: [
        'Shared chores',
        'Shared meal plans',
        'Money items you mark Household',
      ],
      privateScopeTitle: 'Still private',
      privateScopeItems: PRIVATE_SCOPE_ITEMS,
      familySummary: partnerSaved
        ? `${peerName} is already saved in My Settings. Link the household trail later only if you want shared chores, meal plans, or shared Money entries.`
        : 'Save partner details in My Settings if you want them visible in Family, then link the household trail later only if shared admin will help.',
      moneySummary:
        'Money can stay personal. If you link one other adult later, only entries marked Household become visible to both adults. Budgets and connected bank imports stay private.',
    };
  }

  const isOwner = Boolean(input.currentUserId && input.share.ownerUserId === input.currentUserId);

  if (input.share.status === 'active') {
    return {
      state: 'active',
      statusLabel: 'Linked',
      headline: `Linked with ${peerName}`,
      ownershipLabel: isOwner
        ? 'You started this household link.'
        : `${peerName} started this household link.`,
      body:
        'Shared chores and meal plans now follow one household trail. In Money, only entries you mark Household are visible to both adults.',
      nextStep: 'Keep using Household only for items that genuinely belong to both adults.',
      peerName,
      sharedScopeTitle: 'Shared now',
      sharedScopeItems: [
        'Shared chores',
        'Shared meal plans',
        'Money items marked Household',
      ],
      privateScopeTitle: 'Still private',
      privateScopeItems: PRIVATE_SCOPE_ITEMS,
      familySummary: `Family can now reflect the shared household link with ${peerName}, but you still manage partner details and the link itself in My Settings.`,
      moneySummary: `Linked with ${peerName}. In Money, only entries marked Household are visible to both adults. Budgets, budget edits, and connected bank imports stay private to each adult.`,
    };
  }

  return {
    state: 'invited',
    statusLabel: 'Invite ready',
    headline: `Household code ready for ${peerName}`,
    ownershipLabel: isOwner ? 'You started this household link.' : null,
    body:
      'The shared household trail is ready, but it only switches on after the other adult joins with the code.',
    nextStep: `Share the code with ${peerName} to finish the link.`,
    peerName,
    sharedScopeTitle: 'Shares once linked',
    sharedScopeItems: [
      'Shared chores',
      'Shared meal plans',
      'Money items marked Household',
    ],
    privateScopeTitle: 'Still private',
    privateScopeItems: PRIVATE_SCOPE_ITEMS,
    familySummary: `The household code is ready, but Family will only start using the shared household trail after ${peerName} joins it.`,
    moneySummary: `Household sharing is not live yet. When ${peerName} joins, only entries marked Household will be visible to both adults. Budgets and connected bank imports stay private either way.`,
  };
}
